import { defineField, defineGroup, useForm, Validators, type UseForm } from '@/form/useForm';
import type {
  LiabilityFormComputedData,
  LiabilityFormExternalData,
  LiabilityFormLayout,
  LiabilityFormState,
} from './types';
import { getCurrencyOptions } from '@/data/currencies';
import {
  AVAILABLE_LIABILITY_SUBTYPES,
  AVAILABLE_LIABILITY_TYPES,
  calculateLiabilityExpirationDate,
  calculateLiabilityRemainingDebtAndInterest,
  CONVERSION_CURRENCY,
  EAssetType,
  EAttachmentFileType,
  ELiabilityType,
  ETimeDifferenceUnit,
  type FullInvestment,
  type ILiability,
  type ISavedDocument,
} from 'findef';
import { computed, ref, type MaybeRef, type Ref } from 'vue';
import IRClient from '@/modules/ir/apiClient';
import { investmentToOption4 } from '@/form/options/investment';
import { twColor } from '@/utils/style';

import { pushToast } from 'components/toast/hooks';
import { FindexClient } from '@/integrations/findex';
import { getErrorMessage } from '@/utils/error';
import { useAnimationStep } from '@/utils/animation/useAnimationStep';
import { useMemoAsync } from '@/utils/async/useMemoAsync';
import Icon from 'components/icon';
import { LockedByProviderTooltip } from './components/tooltips/lockedByProvider';
import { pushQuickAddAssetModal } from '@/modules/asset/modals/quick-add-asset';

const CURRENCIES = getCurrencyOptions();

export type UseLiabilityForm = {
  form: UseForm<LiabilityFormState, LiabilityFormComputedData>;
  isLoading: Ref<boolean>;
  isSaving: Ref<boolean>;
  isDeleting: Ref<boolean>;
  save: () => Promise<any>;
  deleteLiability: () => Promise<void>;
  setLiabilityType: (liabilityType: ELiabilityType) => void;
  loadingProgress: Ref<number>;
};
export type UseLiabilityFormProps = {
  layout?: MaybeRef<LiabilityFormLayout>;
  liabilityId?: string;
  liability?: ILiability | ISavedDocument<ILiability, string>;
  onDeleted?: () => any;
  currentInvestment?: Ref<FullInvestment>;
};

export const useLiabilityForm = (props: UseLiabilityFormProps): UseLiabilityForm => {
  const isLoading = ref<boolean>(false);
  const isSaving = ref<boolean>(false);
  const isDeleting = ref<boolean>(false);
  const anim = useAnimationStep({
    initial: 0,
    duration: 1.0,
  });
  const loadingProgress = computed(() => anim.current.value);

  const form = useForm<LiabilityFormState, LiabilityFormComputedData, LiabilityFormExternalData>({
    schema: {
      providerImport: defineField({ type: 'dbObject', value: null }),
      investments: defineField({
        type: 'set',
        value: [],
        component: 'multi-select',
        placeholder: 'Select from portfolio',
        hidden: !!props.currentInvestment,
        injectedProps: {
          icon: {
            src: async () => import('assets/img/svg/icons/briefcase.svg?raw'),
            size: '1rem',
            stroke: 'none',
            fill: twColor('gray-400'),
          },
          itemSingularName: 'asset',
          itemPluralName: 'assets',
          manuallyAddAlwaysVisible: true,
          manuallyAddText: 'Quick Add Asset',
          itemHeight: 56,
        },
        options: {
          add: {
            createItem: async (name) => {
              const investment = await pushQuickAddAssetModal({
                assetName: name,
                currentLiability: form.state,
                listed: false,
                assetType: EAssetType.EQUITY,
              });
              if (investment) {
                return investmentToOption4(investment);
              }
            },
          },
          search: {
            api: {
              fetchAll: async () => {
                const investments = await IRClient.getInvestments({ limit: 100 });
                return investments.map((inv) => investmentToOption4(inv));
              },
              fetch: async (queryString) => {
                const investments = await IRClient.getInvestments({
                  queryString: queryString,
                  limit: 100,
                });
                return investments.map((inv) => investmentToOption4(inv));
              },
              fetchSuggestions: async () => {
                const investments = await IRClient.getInvestments({
                  limit: 100,
                });
                return investments.map((inv) => investmentToOption4(inv));
              },
            },
          },
        },
      }),
      hasInvestments: defineField({
        type: 'boolean',
        label: 'Related Assets',
        inlineLabel: true,
        subLabel: 'Toggle if any assets are set as security for this debt',
        value: false,
        disabled: !!props.currentInvestment,
      }),
      alreadyAmortized: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'I Already Amortized',
        },
        fields: {
          value: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
          }),
          type: defineField({
            type: 'text',
            value: 'SEK',
            component: 'selectbox',
            locked: true,
            injectedProps: {
              compact: true,
            },
            options: {
              items: CURRENCIES,
            },
            needs: ['amount.type'],
            next: (state) => {
              return {
                value: state.amount.type || CONVERSION_CURRENCY,
              };
            },
          }),
        },
      }),
      amount: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'Total Borrowed Amount',
          tooltip: 'The total amount borrowed if it is a joint loan with others',
        },
        fields: {
          value: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
            tooltip: 'The total amount borrowed if it is a joint loan with others',
            next: (state, _data, prev) => {
              if (state.providerImport) {
                return {
                  ...prev,
                  value: Math.abs(state.amount.value ?? 0),
                  locked: true,
                  lockedReasonBubbletip: {
                    variant: 'white',
                    placement: 'input-field',
                    noPadding: true,
                    body: () =>
                      state.providerImport ? (
                        <LockedByProviderTooltip
                          providerImport={state.providerImport as unknown as any}
                        />
                      ) : (
                        <div></div>
                      ),
                  },
                };
              }
              return {
                ...prev,
                locked: false,
              };
            },
          }),
          type: defineField({
            type: 'text',
            value: 'SEK',
            component: 'selectbox',
            injectedProps: {
              compact: true,
            },
            options: {
              items: CURRENCIES,
            },
            next: (state, _data, prev) => {
              if (state.providerImport) {
                return {
                  ...prev,
                  value: state.amount.type,
                  locked: true,
                  lockedReasonBubbletip: {
                    variant: 'white',
                    placement: 'input-field',
                    noPadding: true,
                    body: () =>
                      state.providerImport ? (
                        <LockedByProviderTooltip
                          providerImport={state.providerImport as unknown as any}
                        />
                      ) : (
                        <div></div>
                      ),
                  },
                };
              }
              return {
                ...prev,
                locked: false,
              };
            },
          }),
        },
      }),
      quantityPercent: defineField({
        label: 'My Loan Share',
        type: 'number',
        value: 100.0,
        display: {
          value: '%',
        },
        validators: [Validators.range(0, 100, 'Must be between 0 and 100')],
        tooltip:
          'My share of the total borrowed amount. If it is a joint loan with another party, then your share would be 50% and we calculate your loan value accordingly',
      }),
      quantity: defineField({
        label: 'My Loan Share',
        type: 'number',
        value: 1.0,
        display: {
          value: '%',
        },
        needs: ['quantityPercent'],
        next: (state) => ({
          value: (state.quantityPercent ?? 0) / 100.0,
        }),
      }),
      remainingDebt: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'Remaining Debt',
        },
        fields: {
          value: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
            needs: ['amount.value', 'alreadyAmortized.value'],
            next: (state) => {
              const borrowedAmount = state.amount?.value || 0;
              const alreadyAmortized = state.alreadyAmortized?.value || 0;
              const value = borrowedAmount - alreadyAmortized;
              return {
                value: value,
              };
            },
          }),
          type: defineField({
            type: 'text',
            value: 'SEK',
            component: 'selectbox',
            locked: true,
            injectedProps: {
              compact: true,
            },
            options: {
              items: CURRENCIES,
            },
            needs: ['amount.type'],
            next: (state) => {
              return {
                value: state.amount.type || CONVERSION_CURRENCY,
              };
            },
          }),
        },
      }),
      expiryDate: defineField({
        label: 'Maturity Date',
        type: 'date',
        value: new Date(),
        needs: [
          'issueDate',
          'paymentSchedule.occurrence.length',
          'paymentSchedule.occurrence.unit',
        ],
        next: (state) => {
          return {
            value: calculateLiabilityExpirationDate(state) || new Date(),
          };
        },
        injectedProps: {
          maxDate: new Date().setFullYear(new Date().getFullYear() + 100),
        },
      }),
      issueDate: defineField({
        label: 'Loan Issue Date',
        type: 'date',
        value: new Date().setFullYear(new Date().getFullYear() - 1),
        next: (state, _data, prev) => {
          if (state.providerImport) {
            return {
              ...prev,
              value: (state.issueDate || new Date()) as unknown as any,
              locked: true,
              lockedReasonBubbletip: {
                variant: 'white',
                placement: 'input-field',
                noPadding: true,
                body: () =>
                  state.providerImport ? (
                    <LockedByProviderTooltip
                      providerImport={state.providerImport as unknown as any}
                    />
                  ) : (
                    <div></div>
                  ),
              },
            };
          }
          return {
            ...prev,
            locked: false,
            value: (state.issueDate || new Date()) as unknown as any,
          };
        },
      }),
      loanTerm: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'Loan Term',
        },
        fields: {
          length: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
            next: (state, _data, prev) => {
              if (state.providerImport) {
                return {
                  ...prev,
                  value: state.loanTerm.length,
                  locked: true,
                  lockedReasonBubbletip: {
                    variant: 'white',
                    placement: 'input-field',
                    noPadding: true,
                    body: () =>
                      state.providerImport ? (
                        <LockedByProviderTooltip
                          providerImport={state.providerImport as unknown as any}
                        />
                      ) : (
                        <div></div>
                      ),
                  },
                };
              }
              return {
                ...prev,
                locked: false,
                value: state.loanTerm.length,
              };
            },
          }),
          unit: defineField({
            type: 'string',
            component: 'selectbox',
            value: ETimeDifferenceUnit.YEAR,
            injectedProps: { compact: true },
            options: {
              search: { enabled: false },
              items: [
                { label: 'Years', value: ETimeDifferenceUnit.YEAR },
                // { label: 'Half Years', value: ETimeDifferenceUnit.HALF_YEAR },
                { label: 'Months', value: ETimeDifferenceUnit.MONTH },
                { label: 'Weeks', value: ETimeDifferenceUnit.WEEK },
                { label: 'Days', value: ETimeDifferenceUnit.DAY },
              ],
            },
            next: (state, _data, prev) => {
              if (state.providerImport) {
                return {
                  ...prev,
                  value: state.loanTerm.unit,
                  locked: true,
                  lockedReasonBubbletip: {
                    variant: 'white',
                    placement: 'input-field',
                    noPadding: true,
                    body: () =>
                      state.providerImport ? (
                        <LockedByProviderTooltip
                          providerImport={state.providerImport as unknown as any}
                        />
                      ) : (
                        <div></div>
                      ),
                  },
                };
              }
              return {
                ...prev,
                locked: false,
                value: state.loanTerm.unit,
              };
            },
          }),
        },
      }),
      interestRate: defineField({
        label: 'Yearly Interest Rate',
        type: 'number',
        value: 0,
        placeholder: '0',
        display: {
          value: '%',
        },
        next: (state, _data, prev) => {
          if (state.providerImport) {
            return {
              ...prev,
              value: state.interestRate,
              locked: true,
              lockedReasonBubbletip: {
                variant: 'white',
                placement: 'input-field',
                noPadding: true,
                body: () =>
                  state.providerImport ? (
                    <LockedByProviderTooltip
                      providerImport={state.providerImport as unknown as any}
                    />
                  ) : (
                    <div></div>
                  ),
              },
            };
          }
          return {
            ...prev,
            locked: false,
          };
        },
      }),
      name: defineField({
        type: 'text',
        label: 'Debt Name',
        validators: [Validators.required(), Validators.min(3)],
        required: true,
        placeholder: 'e.g. My Mortgage',
        injectedProps: {
          iconLeft: {
            src: async () => import('assets/img/svg/icons/receipt.svg?raw'),
            fill: twColor('gray-400'),
            stroke: 'none',
            size: '1.25rem',
          },
        },
        needs: ['providerImport'],
        next: (state, _data, prev) => {
          return {
            ...prev,
            ...(state.providerImport
              ? {
                  locked: true,
                  lockedReasonBubbletip: {
                    variant: 'white',
                    placement: 'input-field',
                    noPadding: true,
                    body: () =>
                      state.providerImport ? (
                        <LockedByProviderTooltip
                          providerImport={state.providerImport as unknown as any}
                        />
                      ) : (
                        <div></div>
                      ),
                  },
                }
              : {}),
          };
        },
      }),
      value: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'Amount',
        },
        fields: {
          value: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
          }),
          type: defineField({
            type: 'text',
            value: 'SEK',
            component: 'selectbox',
            injectedProps: {
              compact: true,
            },
            options: {
              items: CURRENCIES,
            },
          }),
        },
      }),
      image: defineField({
        type: 'file',
        component: 'attachment',
        label: 'Logo',
        tooltip: 'Placeholder tooltip',
        needs: [],
        //value: state?.image,
        next: (state) => ({
          value: state.image,
          injectedProps: {
            canUpload: true,
            canDelete: true,
            filetype: EAttachmentFileType.IMAGE,
            onRemove: () => {
              console.log('image removed');
            },
            fileUploadOptions: {
              public: true,
            },
            placeholderComponent: () => {
              return (
                <div class="flex items-center justify-center w-8 h-8 bg-gray-100">
                  <Icon
                    src={async () => import('assets/img/svg/icons/receipt.svg?raw')}
                    fill={twColor('gray-400')}
                    size={'2rem'}
                  />
                </div>
              );
            },
          },
        }),
      }),
      ownedByCompany: defineField({
        type: 'boolean',
        label: 'Owned by Company',
        inlineLabel: true,
        subLabel:
          'Toggle of this liability is owned by e.g. a holding company and not directly by you',
        value: false,
        disabled: true,
        locked: true,
        lockedReason: 'Being able to define ownership structure for liabilities is coming soon',
      }),
      parentInvestmentId: defineField({
        type: 'dbObject',
        value: null,
      }),
      paymentSchedule: defineGroup({
        attributes: {
          class: 'grid grid-cols-1 md:grid-cols-2 gap-4'
        },
        fields: {
          value: defineGroup({
            attributes: {
              class: 'grid grid-cols-[auto_7rem]',
              label: 'Amount',
            },
            fields: {
              value: defineField({
                type: 'number',
                value: 0,
                placeholder: '0',
              }),
              type: defineField({
                type: 'text',
                value: 'SEK',
                component: 'selectbox',
                injectedProps: {
                  compact: true,
                },
                options: {
                  items: CURRENCIES,
                },
                locked: true,
                needs: ['amount.type'],
                next: (state) => {
                  return {
                    value: state.amount.type || CONVERSION_CURRENCY,
                  };
                },
              }),
            },
          }),
          occurrence: defineGroup({
            attributes: {
              class: 'grid grid-cols-[auto_7rem]',
              label: 'Every',
            },
            fields: {
              length: defineField({
                type: 'number',
                value: 0,
                placeholder: '0',
              }),
              unit: defineField({
                type: 'string',
                component: 'selectbox',
                value: ETimeDifferenceUnit.YEAR,
                injectedProps: { compact: true },
                options: {
                  search: { enabled: false },
                  items: [
                    { label: 'Years', value: ETimeDifferenceUnit.YEAR },
                    // { label: 'Half Years', value: ETimeDifferenceUnit.HALF_YEAR },
                    { label: 'Months', value: ETimeDifferenceUnit.MONTH },
                    { label: 'Weeks', value: ETimeDifferenceUnit.WEEK },
                    { label: 'Days', value: ETimeDifferenceUnit.DAY },
                  ],
                },
              }),
            },
          }),
          //each: defineField({
          //  label: 'Every',
          //  type: 'number',
          //  value: 1,
          //  placeholder: '0'
          //}),
          //occurrence: defineField({
          //  type: 'string',
          //  component: 'select-dropdown',
          //  value: EIntervalScheduleoccurrence.YEAR,
          //  options: {
          //    search: { enabled: false },
          //    items: [
          //      { label: 'Year', value: EIntervalScheduleoccurrence.YEAR },
          //      { label: 'Half Year', value: EIntervalScheduleoccurrence.HALF_YEAR },
          //      { label: 'Month', value: EIntervalScheduleoccurrence.MONTH },
          //      { label: 'Week', value: EIntervalScheduleoccurrence.WEEK }
          //    ]
          //  }
          //}),
        },
      }),
      subtype: defineField({
        label: 'Type of Debt',
        type: 'string',
        component: 'select-dropdown',
        options: {
          items: AVAILABLE_LIABILITY_SUBTYPES,
        },
        required: true,
        validators: [Validators.required()],
        needs: ['providerImport'],
        next: (state, _data, prev) => {
          return {
            ...prev,
            ...(state.providerImport
              ? {
                  locked: true,
                  lockedReasonBubbletip: {
                    variant: 'white',
                    placement: 'input-field',
                    noPadding: true,
                    body: () =>
                      state.providerImport ? (
                        <LockedByProviderTooltip
                          providerImport={state.providerImport as unknown as any}
                        />
                      ) : (
                        <div></div>
                      ),
                  },
                }
              : {}),
          };
        },
      }),
      type: defineField({
        label: 'Type',
        type: 'string',
        component: 'select-dropdown',
        options: {
          items: AVAILABLE_LIABILITY_TYPES,
        },
      }),
      hasPaymentSchedule: defineField({
        label: 'Set Amortization Schedule?',
        tooltip: 'This is only informative and does not affect any calculations',
        value: false,
        component: 'button-group',
        transform: (x) => {
          if (typeof x === 'boolean') return x;
          return Number(x) === 0 ? true : false;
        },
        transformInverse: (x) => {
          if (typeof x === 'number') return x;
          return x === true ? 0 : 1;
        },
        options: {
          items: [
            { label: 'Yes', value: 0 },
            { label: 'No', value: 1 },
          ],
        },
      }),
    },
    computed: [
      {
        needs: [],
        next: () => {
          //const now = new Date()
          return (state) => {
            //const borrowedAmount = state.value.amount.value
            //const interestRate = (state.value.interestRate ?? 1) / 100.0
            // const scheduleAmount = state.value.paymentSchedule?.value?.value || 0
            //const scheduleEach = state.value.paymentSchedule?.occurrence?.length || 1
            //const scheduleOccurence =
            // state.value?.paymentSchedule?.occurrence?.unit || ETimeDifferenceUnit.YEAR
            //const startDate = state.value.issueDate || new Date()
            //const endDate = state.value.expiryDate || new Date()

            //const loanDates: Date[] = (() => {
            //  switch (scheduleOccurence) {
            //    case ETimeDifferenceUnit.HALF_YEAR:
            //      return getHalfYearsBetween(startDate, endDate)
            //    case ETimeDifferenceUnit.MONTH:
            //      return getMonthsBetween(startDate, endDate)
            //    case ETimeDifferenceUnit.WEEK:
            //      return getWeeksBetween(startDate, endDate)
            //    default:
            //    case ETimeDifferenceUnit.YEAR:
            //      return getYearsBetween(startDate, endDate)
            //  }
            //})()

            //const loanDatesFromNow: Date[] = (() => {
            //  switch (scheduleOccurence) {
            //    case ETimeDifferenceUnit.HALF_YEAR:
            //      return getHalfYearsBetween(now, endDate)
            //    case ETimeDifferenceUnit.MONTH:
            //      return getMonthsBetween(now, endDate)
            //    case ETimeDifferenceUnit.WEEK:
            //      return getWeeksBetween(now, endDate)
            //    default:
            //    case ETimeDifferenceUnit.YEAR:
            //      return getYearsBetween(now, endDate)
            //  }
            //})()

            //const totalDebt = borrowedAmount + borrowedAmount * interestRate
            //const plannedToPay = (loanDates.length + 1) * scheduleAmount * scheduleEach
            //const amountPayed = (loanDatesFromNow.length + 1) * scheduleAmount * scheduleEach
            //const leftToPay = plannedToPay - amountPayed

            ///////////////////////////////////
            //const remainDebt = state.value.remainingDebt?.value || 0;//borrowedAmount - state.value.alreadyAmortized.value || 0
            //const remainInterest = remainDebt * interestRate * loanDatesFromNow.length;

            const calc = calculateLiabilityRemainingDebtAndInterest(
              state.value,
              !!state.value.hasPaymentSchedule,
            );
            //const calc = calculateRemainingDebtAndInterest({
            //  alreadyAmortized: state.value.alreadyAmortized?.value || 0,
            //  amortizationSchedule: !!state.value.hasPaymentSchedule,
            //  borrowedAmount: state.value.amount?.value || 0,
            //  loanIssueDate: state.value.issueDate || new Date(),
            //  loanTerm: state.value.loanTerm?.length || 0,
            //  paymentAmount: state.value.paymentSchedule?.value?.value || 0,
            //  paymentFrequency: state.value.paymentSchedule?.occurrence?.unit === ETimeDifferenceUnit.YEAR ? 1 :
            //                    state.value.paymentSchedule?.occurrence?.unit === ETimeDifferenceUnit.HALF_YEAR ? 2:
            //                    state.value.paymentSchedule?.occurrence?.unit === ETimeDifferenceUnit.MONTH ? 12:
            //                    state.value.paymentSchedule?.occurrence?.unit === ETimeDifferenceUnit.WEEK ? 52 :
            //                    state.value.paymentSchedule?.occurrence?.unit === ETimeDifferenceUnit.DAY ? 360 : 1,
            //  yearlyInterestRate: state.value.interestRate || 0
            //})

            return {
              remainingDebt: { value: calc.remainingDebt || 0 },
              remainingInterest: { value: calc.remainingInterest || 0 },
              myAmount: {
                value: (state.value.amount.value ?? 0) * (state.value.quantity ?? 0),
                type: state.value.amount.type,
              },
            };
          };
        },
      },
    ],
    externalData: {
      currentInvestment: props.currentInvestment,
    },
    layout: props.layout || {},
  });

  const setLiabilityType = (liabilityType: ELiabilityType) => {
    form.setFieldValue('type', liabilityType);
  };

  const liabilityQuery = useMemoAsync(async () => {
    if (props.liability) return props.liability;
    if (!props.liabilityId) return null;
    const [inv] = await FindexClient.getLiabilityById(props.liabilityId);
    if (!inv) return null;
    return inv as ISavedDocument<ILiability, string>;
  });
  const liability = computed((): LiabilityFormState | undefined | null => {
    const liab = liabilityQuery.data;
    if (!liab) return liab;
    return {
      ...liab,
      quantityPercent: (liab.quantity ?? 0) * 100.0,
      hasInvestments: Array.isArray(liab.investments) && liab.investments.length > 0,
    };
  });
  form.watchState(liability, () => {
    form.setFieldValue('isEditMode', true, true);
    form.setFieldValue('type', liability.value?.type || ELiabilityType.DEBT);
    anim.to(1.0, 0.5).then(() => {
      anim.to(0.0, 0.01);
      isLoading.value = false;
    });
  });

  const saveEdits = async (data: LiabilityFormState, liabilityId: string) => {
    if (!liabilityId) {
      console.error(`No liabilityId id`);
      return;
    }

    const cleanedData = { ...data };
    if (!data.hasPaymentSchedule || !data.paymentSchedule?.occurrence?.unit) {
      delete cleanedData.paymentSchedule;
    }

    const [_liab, resp] = await FindexClient.updateLiability(liabilityId, cleanedData);
    if (_liab && !resp?.error) {
      //await IRClient.editInvestment(investmentId, data)
      pushToast({
        title: 'Changes Saved',
        subtitle: `Your edits for ${data.name} was saved`,
        variant: 'success',
      });
    } else {
      console.error(resp?.error);
      pushToast({
        title: 'Error',
        subtitle: 'Failed to save changes, please try again later',
        variant: 'error',
      });
    }
  };

  const saveNew = async (data: LiabilityFormState) => {
    isSaving.value = true;

    const cleanedData = { ...data };
    if (!data.hasPaymentSchedule || !data.paymentSchedule?.occurrence?.unit) {
      delete cleanedData.paymentSchedule;
    }

    const [liab, ctx] = await FindexClient.createLiability(cleanedData);
    if (!liab || ctx?.error) {
      pushToast({
        title: 'Error',
        subtitle: ctx?.error
          ? getErrorMessage(ctx.error, ['message', 'reason'])
          : 'Something went wrong, try again later.',
        variant: 'error',
      });
      isSaving.value = false;
      return null;
    }

    pushToast({
      title: 'Success',
      subtitle: data.name
        ? `${data.name} was added to your portfolio`
        : 'The liability was added to your portfolio',
      variant: 'success',
    });

    isSaving.value = false;

    return liab;
  };

  const save = async (): Promise<any> => {
    const errors = form.errors.value;
    if (errors.length > 0) {
      for (const err of errors) {
        pushToast({
          title: 'Error',
          subtitle: err.message,
          variant: 'error',
        });
      }
      return;
    }

    isSaving.value = true;
    isLoading.value = true;
    anim.to(0.25, 1.0);
    const data = form.cast();
    anim.to(0.5, 0.5);
    console.log({ data });

    if (props.liabilityId) {
      await saveEdits(data, props.liabilityId);
    } else {
      await saveNew(data);
    }
    await anim.to(1.0, 0.5);
    anim.to(0.0, 0.01);
    isLoading.value = false;
    isSaving.value = false;
  };

  const deleteLiability = async () => {
    const id = props.liabilityId;
    if (!id) {
      console.warn(`Missing liabilityId id.`);
      return;
    }
    isDeleting.value = true;
    await anim.to(0.25, 1.0);
    const [ret, resp] = await FindexClient.deleteLiabilityById(id);
    if (!ret || resp?.error) {
      console.error(resp?.error);
      pushToast({
        title: 'Error',
        subtitle: 'Something went wrong, please try again later.',
        variant: 'error',
      });
      isDeleting.value = false;
      anim.to(0.0, 0.01);
      return;
    }
    //try {
    //  await IRClient.deleteInvestment(id)
    //} catch (e) {
    //  console.error(e)
    //  isDeleting.value = true
    //  throw e
    //}
    await anim.to(0.75, 0.5);
    await anim.to(1.0, 1.0);
    anim.to(0.0, 0.01);
    isDeleting.value = false;

    if (props.onDeleted) {
      await props.onDeleted();
    }
  };

  return {
    form,
    isSaving,
    save,
    setLiabilityType,
    deleteLiability,
    isDeleting,
    loadingProgress,
    isLoading,
  };
};
